import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import UserDetailExpanded from '../../user-journey/components/UserDetailExpanded';

/**
 * Full-page user detail view at /:team/activity/users/:distinctId
 * Shows breadcrumb User > distinctId and the 5-tab user detail content
 */
const UserDetailPage = ({ onMenuClick, screenSize }) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { team, distinctId } = params;
  const [resolvedWebsiteId, setResolvedWebsiteId] = useState(location.state?.websiteId ?? null);

  const teamId = useMemo(() => {
    try {
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return selectedTeamData._id || localStorage.getItem('selectedTeam') || '';
    } catch (e) {
      return localStorage.getItem('selectedTeam') || '';
    }
  }, []);

  // Resolve websiteId from state or fetch first website when missing (direct URL)
  useEffect(() => {
    if (location.state?.websiteId) {
      setResolvedWebsiteId(location.state.websiteId);
      return;
    }
    if (!teamId) return;
    const cacheKey = `websites_${teamId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 5 * 60 * 1000 && data?.length > 0) {
          const site = data[0];
          setResolvedWebsiteId(site.id ?? site._id ?? site.siteId ?? site.site_id);
          return;
        }
      } catch (e) {
        // ignore
      }
    }
    const fetchWebsites = async () => {
      try {
        const isObjectId = typeof teamId === 'string' && /^[a-fA-F0-9]{24}$/.test(teamId);
        const response = isObjectId
          ? await axiosInstance.get(`/website/team/${teamId}`)
          : await axiosInstance.get(`/website/name/${localStorage.getItem('selectedTeam') || teamId}`);
        const sites = response?.data?.websites;
        if (sites?.length > 0) {
          const site = sites[0];
          const wid = site.id ?? site._id ?? site.siteId ?? site.site_id;
          setResolvedWebsiteId(wid);
        }
      } catch (err) {
        console.error('Failed to fetch websites for user detail:', err);
      }
    };
    fetchWebsites();
  }, [teamId, location.state?.websiteId]);

  const usersPath = team ? `/${encodeURIComponent(team)}/activity/users` : null;

  const truncateDistinctId = (id) => {
    if (!id || id.length <= 12) return id;
    return `${id.slice(0, 5)}...${id.slice(-5)}`;
  };

  const styles = {
    textPrimary: '#000000',
    textSecondary: '#666666',
    borderColor: '#DDE2EE'
  };

  return (
    <div className="flex flex-col min-h-screen flex-1" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Breadcrumb */}
      <div
        className="px-6 py-4 flex items-center gap-2"
        style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: '#ffffff' }}
      >
        <button
          onClick={() => navigate(usersPath || `/${team}/activity/users`)}
          className="text-sm font-medium hover:underline transition-colors"
          style={{ color: styles.textPrimary }}
        >
          User
        </button>
        <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: styles.textSecondary }} />
        <span className="text-sm" style={{ color: styles.textSecondary }}>
          {truncateDistinctId(distinctId) || distinctId}
        </span>
      </div>

      {/* User detail content */}
      <div className="flex-1 overflow-hidden min-h-0">
        <UserDetailExpanded
          user={{ distinctId: distinctId }}
          teamId={teamId}
          websiteId={resolvedWebsiteId}
        />
      </div>
    </div>
  );
};

export default UserDetailPage;
